// YardsListPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
  Alert,
  Modal,
  Dropdown,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaFilter,
  FaSync,
  FaMapMarkerAlt,
  FaCar,
  FaChartLine,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaTimesCircle,
  FaEllipsisV,
} from "react-icons/fa";
import YardDetailsModal from "./YardDetailsModal";
import axios from "axios";
import { Baseurl } from "../../baseurl";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
// import api from '../../services/api';

const YardsListPage = () => {
  const navigate = useNavigate();
  const { loginData } = useSelector((store) => store.auth);
  const [yards, setYards] = useState([]);
  const [filteredYards, setFilteredYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [yardToDelete, setYardToDelete] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedYard, setSelectedYard] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "yardName",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch yards data
  useEffect(() => {
    fetchYards();
  }, []);

  // Filter and sort yards when dependencies change
  useEffect(() => {
    let result = [...yards];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        (yard) =>
          yard.yardName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          yard.yardAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
          yard.city.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((yard) => yard.yardStatus === statusFilter);
    }

    // Apply city filter
    if (cityFilter !== "all") {
      result = result.filter((yard) => yard.city === cityFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key === "parkedVehicles") {
          aValue = a.parkedVehicles || 0;
          bValue = b.parkedVehicles || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredYards(result);
  }, [yards, searchTerm, statusFilter, cityFilter, sortConfig]);

  const fetchYards = async () => {
    try {
      if (!loginData) return;

      setLoading(true);

      const agencyId =
        loginData.role === "Agency"
          ? loginData._id
          : loginData.organisation?.id;

      if (!agencyId) return;
      const response = await axios.get(
        `${Baseurl}/api/yards/getAllYardForAdmin/${agencyId}`,
      );
      setYards(response.data?.yards);
      setError(null);
    } catch (err) {
      setError("Failed to fetch yards. Please try again.");
      console.error("Error fetching yards:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleDeleteClick = (yard) => {
    setYardToDelete(yard);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const resp = await axios.delete(`${Baseurl}/api/yards/deleteYard`, {
        _id: yardToDelete._id,
      });
      if (resp.data.success) {
        toast.success(`${resp.data.message}`);
        setYards(yards.filter((yard) => yard._id !== yardToDelete._id));
        setShowDeleteModal(false);
        setYardToDelete(null);
      }
    } catch (err) {
      console.error("Error deleting yard:", err);
    }
  };

  const handleViewDetails = (yard) => {
    setSelectedYard(yard);
    setShowDetailsModal(true);
  };

  const handleEditYard = (yard) => {
    navigate(`/yards/edit/${yard._id}`);
  };

  // Get unique cities for filter
  const cities = [...new Set(yards.map((yard) => yard.city).filter(Boolean))];

  // Calculate pagination
  const totalPages = Math.ceil(filteredYards.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredYards.slice(indexOfFirstItem, indexOfLastItem);

  const getStatusBadge = (status) => {
    switch (status) {
      case "operational":
        return <Badge bg="success">Operational</Badge>;
      case "maintenance":
        return <Badge bg="warning">Maintenance</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getAvailabilityBadge = (parked, occupancy) => {
    const percentage = occupancy ? Math.round((parked / occupancy) * 100) : 0;

    if (percentage >= 90) return <Badge bg="danger">{percentage}% Full</Badge>;
    if (percentage >= 70) return <Badge bg="warning">{percentage}% Full</Badge>;
    return <Badge bg="success">{percentage}% Full</Badge>;
  };

  if (loading && yards.length === 0) {
    return (
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading yards...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Parking Yards</h2>
              <p className="text-muted mb-0">
                Manage all parking yard locations
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={fetchYards}>
                <FaSync className="me-2" />
                Refresh
              </Button>
              <Button variant="primary" onClick={() => navigate("/yards/addYard")}>
                <FaPlus className="me-2" />
                Add New Yard
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Yards</h6>
                  <h3 className="mb-0">{yards.length}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <FaMapMarkerAlt className="text-primary" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Occupied Spots</h6>
                  <h3 className="mb-0">
                    {yards.reduce(
                      (acc, yard) => acc + (yard.parkedVehicles || 0),
                      0,
                    )}
                  </h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaCar className="text-success" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Operational</h6>
                  <h3 className="mb-0">
                    {yards.filter((y) => y.yardStatus === "operational").length}
                  </h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <FaCheckCircle className="text-info" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Verified</h6>
                  <h3 className="mb-0">
                    {yards.filter((y) => y.isVerified).length}
                  </h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FaChartLine className="text-warning" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, address, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="operational">Operational</option>
                <option value="maintenance">Maintenance</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="all">All Cities</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setCityFilter("all");
                }}
                className="w-100"
              >
                <FaFilter className="me-2" />
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Yards Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "30px" }}>#</th>
                  <th
                    onClick={() => handleSort("yardName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center">
                      Yard Name
                      <span className="ms-2">{getSortIcon("yardName")}</span>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("city")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center">
                      Location
                      <span className="ms-2">{getSortIcon("city")}</span>
                    </div>
                  </th>
                  <th>Status</th>
                  <th
                    onClick={() => handleSort("parkedVehicles")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center">
                      Availability
                      <span className="ms-2">
                        {getSortIcon("parkedVehicles")}
                      </span>
                    </div>
                  </th>
                  <th>Verification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <div className="text-muted">
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading yards...
                          </>
                        ) : (
                          "No yards found. Try adjusting your filters or add a new yard."
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((yard, index) => (
                    <tr key={yard._id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div>
                          <strong>{yard.yardName}</strong>
                          <div className="text-muted small">
                            {yard.yardAddress}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>
                            {yard.city}, {yard.state}
                          </div>
                          <small className="text-muted">
                            Lat: {yard.coordinates?.lat?.toFixed(4)}, Lng:{" "}
                            {yard.coordinates?.lng?.toFixed(4)}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {getStatusBadge(yard.yardStatus)}
                          <Badge bg={yard.isActive ? "success" : "secondary"}>
                            {yard.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {getAvailabilityBadge(
                            yard.parkedVehicles,
                            yard.occupancy,
                          )}
                          <small>
                            {yard.parkedVehicles || 0} / {yard.occupancy || 0}{" "}
                            vehicles
                          </small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {yard.isVerified ? (
                            <>
                              <FaCheckCircle className="text-success me-1" />
                              <span>Verified</span>
                            </>
                          ) : (
                            <>
                              <FaTimesCircle className="text-danger me-1" />
                              <span>Unverified</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View Details</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(yard)}
                            >
                              <FaEye />
                            </Button>
                          </OverlayTrigger>

                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Edit Yard</Tooltip>}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleEditYard(yard)}
                            >
                              <FaEdit />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Edit Yard</Tooltip>}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleDeleteClick(yard)}
                            >
                              <FaTrash className="me-2" />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer>
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, filteredYards.length)} of{" "}
                {filteredYards.length} yards
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                />
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the yard "
          <strong>{yardToDelete?.yardName}</strong>"?
          <Alert variant="warning" className="mt-3">
            <strong>Warning:</strong> This action cannot be undone. All parking
            spots and associated data will be removed.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete Yard
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Yard Details Modal */}
      <YardDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        yard={selectedYard}
      />
    </Container>
  );
};

export default YardsListPage;
